import { HttpClient } from '@angular/common/http';
import { LOCALE_ID, NgModule } from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppTranslateLoader } from './app-translate-loader';
import { EnvConfig, LocaleId } from './config';
import { getLocaleKey, DEFAULT_LOCALE } from './translate-utils';
import { LanguageService } from './language/language.service';

@NgModule({
  imports: [
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: AppTranslateLoader,
        deps: [HttpClient, EnvConfig],
      },
    }),
  ],
  exports: [TranslateModule],
  providers: [
    {
      provide: LOCALE_ID,
      useFactory: (env: EnvConfig) => env.enforcedLocaleId ?? getLocaleKey(navigator.language),
      deps: [EnvConfig],
    },
    LanguageService,
  ],
})
export class AppTranslateModule {
  constructor(translate: TranslateService, env: EnvConfig) {
    // Clear stored language if it wasn't explicitly set by user
    // This prevents browser default language from persisting
    const wasExplicitlySet = localStorage.getItem('wai_language_explicitly_set');

    if (!wasExplicitlySet) {
      localStorage.removeItem('wai_user_language');
    }

    const localeKey: LocaleId = this.getInitialLanguage(env);

    translate.setDefaultLang(DEFAULT_LOCALE);
    translate.use(localeKey);

    // Mark language as explicitly set to prevent theme from overriding it
    // This ensures the initialized language persists even when theme loads later
    localStorage.setItem('wai_language_explicitly_set', 'true');
    localStorage.setItem('wai_user_language', localeKey);
  }

  private getInitialLanguage(env: EnvConfig): LocaleId {
    if (env.enforcedLocaleId) {
      return env.enforcedLocaleId;
    }

    const storedLanguage = this.getStoredLanguage();
    if (storedLanguage) {
      return storedLanguage;
    }

    // Check if theme has a default language stored
    const themeDefaultLanguage = this.getThemeDefaultLanguage();
    if (themeDefaultLanguage) {
      return themeDefaultLanguage;
    }

    // Fallback to default locale
    return DEFAULT_LOCALE;
  }

  private getStoredLanguage(): LocaleId | null {
    try {
      const stored = localStorage.getItem('wai_user_language');
      // Allow any language code - backend handles all languages dynamically
      if (stored) {
        return stored as LocaleId;
      }
    } catch (error) {
      console.warn('Could not read language preference from localStorage:', error);
    }
    return null;
  }

  private getThemeDefaultLanguage(): LocaleId | null {
    try {
      const themeData = localStorage.getItem('initialThemeParams');

      if (themeData) {
        const theme = JSON.parse(themeData);

        if (theme.availableLanguages && Array.isArray(theme.availableLanguages)) {
          const defaultLang = theme.availableLanguages.find((lang: any) => lang.isDefault);

          if (defaultLang && defaultLang.code) {
            return defaultLang.code as LocaleId;
          }
        }
      }
    } catch (error) {
      console.warn('Could not read theme default language:', error);
    }
    return null;
  }
}
